import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCw, Eye, EyeOff, Copy, Check } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/app/components/ui/radio-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/app/components/ui/alert-dialog';
import axios from 'axios';
import { useProvisionUser } from '@/hooks/use-crew';
import { handleApiError } from '@/lib/api-client';
import { useUpdateOnboardingSteps, useOnboardingState } from '@/hooks/use-onboarding';
import { usePlatformModules } from '@/hooks/use-modules';
import { getModuleDisplayName, getPlatformModules } from '@/lib/modules';
import { CompanyRoleSelectItems } from '@/app/components/company-role-select-items';
import type { ProvisionMode, ProvisionUserPayload, UserRole, EmploymentType, ExistingPersonRef, ModuleRoleLevel } from '@/lib/types';

const MODULE_ROLE_OPTIONS: { value: ModuleRoleLevel; label: string; description: string }[] = [
  { value: 'no_access', label: 'No Access', description: 'Cannot access this module' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access' },
  { value: 'user', label: 'User', description: 'Can interact with documents' },
  { value: 'supervisor', label: 'Supervisor', description: 'Can create documents and run talks' },
  { value: 'manager', label: 'Manager', description: 'Full access except destructive actions' },
  { value: 'admin', label: 'Admin', description: 'Full access' },
];

function generatePassword(length = 16): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%&*';
  const all = upper + lower + digits + special;

  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);

  let pwd = '';
  // Ensure at least one of each required type
  pwd += upper[arr[0] % upper.length];
  pwd += lower[arr[1] % lower.length];
  pwd += digits[arr[2] % digits.length];
  pwd += special[arr[3] % special.length];

  for (let i = pwd.length; i < length; i++) {
    pwd += all[arr[i] % all.length];
  }

  // Fisher-Yates shuffle using crypto-secure randomness
  const chars = pwd.split('');
  const shuffleArr = new Uint32Array(chars.length);
  crypto.getRandomValues(shuffleArr);
  for (let i = chars.length - 1; i > 0; i--) {
    const j = shuffleArr[i] % (i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
}

export function AddEmployeePage({ basePath = '/people' }: { basePath?: string } = {}) {
  const navigate = useNavigate();
  const provisionMutation = useProvisionUser();
  const updateOnboardingSteps = useUpdateOnboardingSteps();
  const { data: onboardingState } = useOnboardingState();
  const { enabledModules } = usePlatformModules();
  const platformKeys = getPlatformModules().map(m => m.key);
  const topLevelModules = enabledModules.filter(mod => platformKeys.includes(mod));
  const hasModuleRoles = topLevelModules.length > 0;

  const [mode, setMode] = useState<ProvisionMode>('temp_password');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [phone, setPhone] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [employmentType, setEmploymentType] = useState<EmploymentType>('full_time');
  const [role, setRole] = useState<UserRole>('User');
  const [roleManuallySelected, setRoleManuallySelected] = useState(false);
  const [password, setPassword] = useState(() => generatePassword());
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [createTechProfile, setCreateTechProfile] = useState(true);
  const [moduleRoles, setModuleRoles] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [existingPerson, setExistingPerson] = useState<ExistingPersonRef | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [adminPromotionOpen, setAdminPromotionOpen] = useState(false);

  const updateModuleRole = (moduleKey: string, roleValue: string) => {
    setModuleRoles((prev) => {
      const next = { ...prev };
      if (roleValue === 'no_access') {
        delete next[moduleKey];
      } else {
        next[moduleKey] = roleValue;
      }
      return next;
    });
  };

  const handleGeneratePassword = () => {
    setPassword(generatePassword());
    setCopied(false);
  };

  const handleCopyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback: select the input text
    }
  };

  const submitEmployee = async (confirmAdminGrant = false) => {
    setError(null);
    setExistingPerson(null);
    setSuccess(null);

    if (!firstName || !lastName || !email) {
      setError('First name, last name, and email are required.');
      return;
    }

    const payload: ProvisionUserPayload = {
      first_name: firstName,
      last_name: lastName,
      email,
      username: username || undefined,
      mode,
      role,
      create_tech_profile: createTechProfile,
      job_title: jobTitle || undefined,
      employment_type: employmentType,
      phone: phone || undefined,
      module_roles: Object.keys(moduleRoles).length > 0 ? moduleRoles : undefined,
    };

    if (confirmAdminGrant) {
      payload.confirm_admin_grant = true;
    }

    if (mode === 'temp_password') {
      payload.password = password;
    }

    try {
      const result = await provisionMutation.mutateAsync(payload);

      // Mark the "Invite first employee" onboarding step as done
      // (only if the step exists — H3ightboard-only orgs skip it)
      if (onboardingState?.steps?.some((s) => s.key === 'first_employee')) {
        updateOnboardingSteps.mutate({ steps: { first_employee: true } });
      }

      if (result.tech_profile_id) {
        // Redirect back to onboarding wizard if onboarding is still in progress
        if (onboardingState && onboardingState.is_admin && !onboardingState.onboarding_completed) {
          navigate('/onboarding');
        } else {
          navigate(`${basePath}/${result.tech_profile_id}`);
        }
      } else {
        setSuccess(result.message);
      }
    } catch (err) {
      const apiError = handleApiError(err);
      // Handle 409 conflict (duplicate email)
      if (typeof apiError === 'object' && 'message' in apiError) {
        setError(apiError.message);
      } else {
        setError(String(apiError));
      }
      // Duplicate-email 409s include the existing person so we can link to their card
      if (axios.isAxiosError(err)) {
        const data = err.response?.data as { existing_person?: ExistingPersonRef } | undefined;
        if (data?.existing_person) {
          setExistingPerson(data.existing_person);
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setExistingPerson(null);
    setSuccess(null);

    if (!firstName || !lastName || !email) {
      setError('First name, last name, and email are required.');
      return;
    }

    if (role === 'Admin' && mode !== 'no_login') {
      setAdminPromotionOpen(true);
      return;
    }

    await submitEmployee();
  };

  const handleConfirmAdminPromotion = () => {
    setAdminPromotionOpen(false);
    void submitEmployee(true);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button asChild variant="ghost" size="icon">
          <Link to={basePath}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Add Employee</h1>
          <p className="text-gray-600 mt-1">Create a new employee with user account and tech profile</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 max-w-3xl">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">
            {error}
            {existingPerson && (
              <p className="mt-1">
                <Link
                  to={`${basePath}?q=${encodeURIComponent(existingPerson.name)}`}
                  className="font-medium underline"
                >
                  View {existingPerson.name}&apos;s card
                </Link>{' '}
                to invite them to the platform instead of re-entering their details.
              </p>
            )}
          </div>
        )}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 p-3 rounded-lg text-sm">
            {success}
            {onboardingState && onboardingState.is_admin && !onboardingState.onboarding_completed && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={() => navigate('/onboarding')}
              >
                Continue Setup
              </Button>
            )}
          </div>
        )}

        {/* Account Setup Mode */}
        <Card>
          <CardHeader>
            <CardTitle>Account Setup</CardTitle>
            <CardDescription>Choose how this employee will access the system</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <RadioGroup
              value={mode}
              onValueChange={(v) => setMode(v as ProvisionMode)}
              className="space-y-3"
            >
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="temp_password" id="mode-temp" className="mt-0.5" />
                <div>
                  <Label htmlFor="mode-temp" className="font-medium cursor-pointer">
                    Set temporary password
                  </Label>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Create account with a password they must change on first login
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="invite_link" id="mode-invite" className="mt-0.5" />
                <div>
                  <Label htmlFor="mode-invite" className="font-medium cursor-pointer">
                    Send invitation email
                  </Label>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Send an email invitation so they can set up their own password
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition-colors">
                <RadioGroupItem value="no_login" id="mode-nologin" className="mt-0.5" />
                <div>
                  <Label htmlFor="mode-nologin" className="font-medium cursor-pointer">
                    No login needed
                  </Label>
                  <p className="text-sm text-gray-500 mt-0.5">
                    Create a tech profile only &mdash; no user account or system access
                  </p>
                </div>
              </div>
            </RadioGroup>

            {/* Temp password field */}
            {mode === 'temp_password' && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-2">
                <Label htmlFor="password">Temporary Password</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        setCopied(false);
                      }}
                      className="pr-10 font-mono"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <Button type="button" variant="outline" size="icon" onClick={handleGeneratePassword} title="Generate new password">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="icon" onClick={handleCopyPassword} title="Copy password">
                    {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-gray-500">
                  The employee will be required to change this password on first login.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card>
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first_name">First Name *</Label>
                <Input
                  id="first_name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label htmlFor="last_name">Last Name *</Label>
                <Input
                  id="last_name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => {
                    const newEmail = e.target.value;
                    setEmail(newEmail);
                    // Auto-generate username from email prefix if not manually edited
                    if (!usernameManuallyEdited && newEmail.includes('@')) {
                      setUsername(newEmail.split('@')[0].toLowerCase());
                    } else if (!usernameManuallyEdited) {
                      setUsername(newEmail.toLowerCase());
                    }
                  }}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            {mode === 'temp_password' && (
              <div>
                <Label htmlFor="username">Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => {
                    setUsername(e.target.value);
                    setUsernameManuallyEdited(true);
                  }}
                  placeholder="Auto-generated from email"
                />
                <p className="text-xs text-gray-500 mt-1">
                  This is the login username. Auto-generated from email if left unchanged.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Employment Information */}
        <Card>
          <CardHeader>
            <CardTitle>Employment Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="job_title">Job Title</Label>
                <Input
                  id="job_title"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="employment_type">Employment Type</Label>
                <Select
                  value={employmentType}
                  onValueChange={(v) => setEmploymentType(v as EmploymentType)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_time">Full Time</SelectItem>
                    <SelectItem value="part_time">Part Time</SelectItem>
                    <SelectItem value="contractor">Contractor</SelectItem>
                    <SelectItem value="subcontractor">Subcontractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {mode !== 'no_login' && (
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="checkbox"
                  id="create_tech"
                  checked={createTechProfile}
                  onChange={(e) => {
                    setCreateTechProfile(e.target.checked);
                    if (!roleManuallySelected) {
                      setRole(e.target.checked ? 'User' : 'Viewer');
                    }
                  }}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="create_tech" className="text-sm cursor-pointer">
                  Also create a tech profile for field work tracking
                </Label>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Role & Permissions (only for modes that create a user account) */}
        {mode !== 'no_login' && (
          <Card>
            <CardHeader>
              <CardTitle>Role & Permissions</CardTitle>
              <CardDescription>Set the organization role and per-module access levels</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="role">Organization Role</Label>
                <Select
                  value={role}
                  onValueChange={(v) => {
                    setRole(v as UserRole);
                    setRoleManuallySelected(true);
                  }}
                >
                  <SelectTrigger id="role" className="w-full sm:w-64">
                    <SelectValue>{role}</SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <CompanyRoleSelectItems />
                  </SelectContent>
                </Select>
              </div>

              {hasModuleRoles && (
                <div>
                  <Label className="mb-2 block">Module Access</Label>
                  <div className="border rounded-lg divide-y">
                    {topLevelModules.map((mod) => (
                      <div key={mod} className="flex items-center justify-between px-4 py-3">
                        <span className="text-sm font-medium">{getModuleDisplayName(mod)}</span>
                        <Select
                          value={moduleRoles[mod] || 'no_access'}
                          onValueChange={(v) => updateModuleRole(mod, v)}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {MODULE_ROLE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <div className="flex gap-3">
          <Button type="submit" disabled={provisionMutation.isPending}>
            {provisionMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {mode === 'temp_password' && 'Create Employee'}
            {mode === 'invite_link' && 'Send Invitation'}
            {mode === 'no_login' && 'Add Tech Profile'}
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to={basePath}>Cancel</Link>
          </Button>
        </div>
      </form>
      <AlertDialog open={adminPromotionOpen} onOpenChange={setAdminPromotionOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote user to Admin?</AlertDialogTitle>
            <AlertDialogDescription>
              Admins have full access to company settings, users, and every enabled module. Promote{' '}
              {[firstName.trim(), lastName.trim()].filter(Boolean).join(' ') || email} only if they
              should have that access.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmAdminPromotion}>
              Promote to Admin
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
